import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import MobileMenuBar from '../MobileMenuBar';
import CartIcon from '../CartIcon';
import { HomeOutlined, ShoppingOutlined, HeartOutlined, ContactsOutlined } from '@ant-design/icons';

const navConfigurations = [
  { key: 'home', label: 'Home', path: '/', icon: <HomeOutlined /> },
  { key: 'shop', label: 'Shop', path: '/shop', icon: <ShoppingOutlined /> },
  { key: 'about', label: 'About', path: '/about-us', icon: <HeartOutlined /> },
  { key: 'contact', label: 'Contact', path: '/contact-us', icon: <ContactsOutlined /> },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY >= 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActivePath = (path: string) => {
    if (path === '/') {
      return router.pathname === '/';
    }
    return router.pathname.startsWith(path);
  };

  return (
    <header style={{ position: 'relative' }} aria-label="Main site header">
      <motion.div
        initial={{ y: -101 }}
        animate={{ y: isScrolled ? 0 : -101 }}
        transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }}
        style={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 999, opacity: 1 }}
      >
        <div className={`ch-header ${isScrolled ? 'ch-fixed-header' : ''}`}>
          <div className="ch-header-left">
            <MobileMenuBar navConfigurations={navConfigurations} />
          </div>
          <motion.div
            className='ch-header-logo'
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75 }}
          >
            <Image 
                src='/assets/images/chethisLogo.svg' 
                alt='Chethis logo' 
                fill 
                priority
            />
          </motion.div>
          <motion.div
            className="ch-header-cart mobile"
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.1 }}
          >
            <CartIcon />
          </motion.div>
          <div className='ch-header-nav desktop' role="navigation" aria-label="Main navigation">
            {navConfigurations.map(navigation => (
              <motion.div
                key={navigation.key}
                className={`ch-header-nav-item ${isActivePath(navigation.path) ? 'active' : ''}`}
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75 }}
                onClick={() => router.push(navigation.path)}
                tabIndex={0}
                role="link"
                aria-label={navigation.label}
              >
                {navigation.label}
              </motion.div>
            ))}
            <motion.div
              className="ch-header-cart"
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.1 }}
            >
              <CartIcon />
            </motion.div>
          </div>
          {/* Decorative luxury gradient bar */}
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #ede9e0 0%, #d4c4a8 50%, #f8f7f6 100%)',
            opacity: 0.7,
            borderRadius: '0 0 12px 12px',
            boxShadow: '0 2px 8px rgba(139, 118, 96, 0.15)'
          }} aria-hidden="true" />
        </div>
      </motion.div>
      <div style={{ visibility: isScrolled ? 'hidden' : 'visible' }}>
        <div className='ch-header'>
          <div className="ch-header-left">
            <MobileMenuBar navConfigurations={navConfigurations} />
          </div>
          <motion.div
            className='ch-header-logo'
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75 }}
          >
            <Image src='/assets/images/chethisLogo.svg' alt='Chethis logo' fill priority />
          </motion.div>
          <motion.div
            className="ch-header-cart mobile"
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.1 }}
          >
            <CartIcon />
          </motion.div>
          <div className='ch-header-nav desktop' role="navigation" aria-label="Main navigation">
            {navConfigurations.map(navigation => (
              <motion.div
                key={navigation.key}
                className={`ch-header-nav-item ${isActivePath(navigation.path) ? 'active' : ''}`}
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75 }}
                onClick={() => router.push(navigation.path)}
                tabIndex={0}
                role="link"
                aria-label={navigation.label}
              >
                {navigation.label}
              </motion.div>
            ))}
            <motion.div
              className="ch-header-cart"
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.1 }}
            >
              <CartIcon />
            </motion.div>
          </div>
          {/* Decorative luxury gradient bar */}
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #ede9e0 0%, #d4c4a8 50%, #f8f7f6 100%)',
            opacity: 0.7,
            borderRadius: '0 0 12px 12px',
            boxShadow: '0 2px 8px rgba(139, 118, 96, 0.15)'
          }} aria-hidden="true" />
        </div>
      </div>
    </header>
  );
};

export default Header;